import {
  PRIORITY_DOMAIN_NAMES,
  isPriorityDomain,
  type DomainGroup,
  type DomainRecord,
} from '@brainpool/profile-schema';

// ─── CV / LinkedIn data parsers ───────────────────────────────────────────────

/** Extract domain groups from CV-parsed profile_metadata_json */
export const parseCvObjects = (metadataJson: string | null): DomainGroup[] => {
  if (!metadataJson) return [];
  try {
    const metadata = JSON.parse(metadataJson) as {
      cvObjects?: Array<{ domain: string; record: Record<string, unknown> }>;
    };
    const cvObjects = Array.isArray(metadata.cvObjects) ? metadata.cvObjects : [];
    const grouped: Record<string, DomainRecord[]> = {};
    for (const obj of cvObjects) {
      const domain = obj.domain?.toUpperCase();
      if (!domain || !isPriorityDomain(domain)) continue;
      if (!grouped[domain]) grouped[domain] = [];
      grouped[domain].push(obj.record as DomainRecord);
    }
    return Object.entries(grouped).map(([domain, records]) => ({ domain, records }));
  } catch {
    return [];
  }
};

/** Extract domain groups from LinkedIn DMA snapshot (envelope format) */
export const parseLinkedinObjects = (linkedinDataJson: string | null): DomainGroup[] => {
  if (!linkedinDataJson) return [];
  try {
    const data = JSON.parse(linkedinDataJson) as {
      envelope?: {
        elements?: Array<{
          snapshotDomain: string;
          snapshotData?: Array<{ domainSpecificRecord?: Record<string, unknown> }>;
        }>;
      };
    };
    const elements = data?.envelope?.elements ?? [];
    const grouped: Record<string, DomainRecord[]> = {};
    for (const el of elements) {
      const domain = el.snapshotDomain?.toUpperCase();
      if (!domain || !isPriorityDomain(domain)) continue;
      const records = (el.snapshotData ?? [])
        .map(sd => sd.domainSpecificRecord)
        .filter((r): r is Record<string, unknown> => !!r) as DomainRecord[];
      if (records.length > 0) {
        if (!grouped[domain]) grouped[domain] = [];
        grouped[domain].push(...records);
      }
    }
    return Object.entries(grouped).map(([domain, records]) => ({ domain, records }));
  } catch {
    return [];
  }
};

/**
 * Build the initial domain groups for the editor.
 * If the user has already saved edits (userObjects), those take priority.
 * Otherwise, merge CV-extracted objects and LinkedIn data.
 */
export const initDomainGroups = (
  metadataJson: string | null,
  linkedinDataJson: string | null
): DomainGroup[] => {
  if (metadataJson) {
    try {
      const metadata = JSON.parse(metadataJson) as {
        userObjects?: Array<{ domain: string; record: Record<string, unknown> }>;
      };
      if (Array.isArray(metadata.userObjects) && metadata.userObjects.length > 0) {
        const grouped: Record<string, DomainRecord[]> = {};
        for (const obj of metadata.userObjects) {
          const domain = obj.domain?.toUpperCase();
          if (!domain || !isPriorityDomain(domain)) continue;
          if (!grouped[domain]) grouped[domain] = [];
          grouped[domain].push(obj.record as DomainRecord);
        }
        return Object.entries(grouped).map(([domain, records]) => ({ domain, records }));
      }
    } catch {
      /* fall through to CV + LinkedIn merge */
    }
  }

  const cvGroups = parseCvObjects(metadataJson);
  const linkedinGroups = parseLinkedinObjects(linkedinDataJson);

  const merged: Record<string, DomainRecord[]> = {};
  for (const group of [...linkedinGroups, ...cvGroups]) {
    if (!merged[group.domain]) merged[group.domain] = [];
    merged[group.domain].push(...group.records);
  }
  return Object.entries(merged).map(([domain, records]) => ({ domain, records }));
};

/** Flatten grouped domain objects into the flat array format the API expects */
export const flattenDomainGroups = (
  groups: DomainGroup[]
): Array<{ domain: string; record: DomainRecord }> =>
  groups.flatMap(g => g.records.map(record => ({ domain: g.domain, record })));

// ─── UI formatting helpers ────────────────────────────────────────────────────

/** "PHONE_NUMBERS" → "Phone Numbers" */
export const formatDomainName = (domain: string): string =>
  domain
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

/**
 * Returns the ordered list of visible domain names for the editor/viewer.
 * Preserves the curated PRIORITY_DOMAIN_NAMES order.
 */
export const getPriorityDomainList = (): string[] => [...PRIORITY_DOMAIN_NAMES];
